import {useRef, useState} from "react";
import styled from "styled-components";
import theme from "../../style/theme";
import {CarType} from "../../types/routing-service/vehicle";
import ExpandLessIcon from "../icons/ExpandLessIcon/ExpandLessIcon";
import ExpandMoreIcon from "../icons/ExpandMoreIcon/ExpandMoreIcon";
import {useClickOutside} from "../../utils/click/handle-click";

interface Props {
    value: CarType;
    candidateMap: { [key: string]: string };
    onClickItem: (key: CarType) => void;
}

export const CarTypeSelector: React.FC<Props> = (
    {
        value,
        onClickItem,
        candidateMap,
    }
) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const carMap = {
        [CarType.Ray]: "레이(100)",
        [CarType.Starex]: "스타렉스(150)",
    };

    const handleClickBox = () => {
        setOpen(!open);
    };

    useClickOutside(ref, () => setOpen(false));

    return (
        <Container ref={ref}>
            <SelectedItem onClick={handleClickBox}>
                <SelectedItemLabel>{carMap[value]}</SelectedItemLabel>
                <RightSelectedItemIconWrapper>
                    {open ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                </RightSelectedItemIconWrapper>
            </SelectedItem>
            {open && (
                <MenuItemList>
                    {Object.keys(carMap).map((candidate) => {
                        const isSelected = value === candidate;
                        return (
                            <MenuItem
                                key={candidate}
                                selected={isSelected}
                                onClick={() => {
                                    onClickItem(candidate as CarType);
                                    setOpen(false);
                                }}
                            >
                                <MenuItemLabel>{candidateMap[candidate]}</MenuItemLabel>
                            </MenuItem>
                        );
                    })}
                </MenuItemList>
            )}
        </Container>
    );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  position: relative;
`;

interface SelectedItemProps {
    width?: string;
}

const SelectedItem = styled.div<SelectedItemProps>`
  width: 145px;
  height: 2.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  border-radius: 4px;
  border: ${`1px solid ${theme.palette.primitives.gray300}`};
  background-color: ${theme.palette.primitives.white};

  &:hover {
    background-color: #f5f6f9;
  }
`;

const SelectedItemLabel = styled.div`
  height: 2.625rem;
  margin-left: 0.75rem;
  font-style: normal;
  font-weight: 500;
  font-size: 0.938rem;
  color: ${theme.palette.primitives.gray700};
  display: flex;
  align-items: center;
`;

const RightSelectedItemIconWrapper = styled.div`
  width: 36px;
  height: 24px;
  box-sizing: border-box;
  padding: 0 12px 0 7px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-left: 1px solid ${theme.palette.primitives.gray200};
`;

interface MenuItemListProps {
    width?: string;
}

const MenuItemList = styled.div<MenuItemListProps>`
  width: 145px;
  max-height: 12.188rem;
  padding: 6.5px 0;
  position: absolute;
  background-color: #ffffff;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.2);
  border: solid 1px #bec3cb;
  z-index: 500;
  // max-height: calc(100vh - 400px);
  overflow-y: hidden;
  overflow-x: hidden;

  /* 스크롤 바 스타일 */

  &::-webkit-scrollbar {
    width: 5px;
    background-color: #ffffff;
  }

  /* 스크롤바 색상 */

  &::-webkit-scrollbar-thumb {
    background-color: #1c2026;
    border-radius: 10px;
  }

  /* 스크롤바가 마우스 오버될 때 색상 */

  &::-webkit-scrollbar-thumb:hover {
    background-color: #bfbfbf;
  }
`;

interface MenuItemProps {
    selected: boolean;
}

const MenuItem = styled.div<MenuItemProps>`
  height: 36px;
  display: flex;
  color: #3f444e;
  align-items: center;
  background-color: ${(props) =>
          props.selected ? theme.palette.primitives.yellow50 : "#ffffff"};

  &:hover {
    background-color: #f5f6f9;
  }

  font-size: 14px;
  font-weight: 500;
`;

const MenuItemLabel = styled.div`
  height: 2.5rem;
  margin-left: 0.75rem;
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.palette.primitives.black};
  display: flex;
  align-items: center;
`;
